// features/findings/registry.js
// ════════════════════════════════════════════════════════════════════
// FINDING REGISTRY — version-aware (Phase F6)
//
// Registry keys are versioned: <domain>/<year>/<slug>/<version>
// Lookups support both versioned and bare-slug forms:
//   getFinding('strategic', 2026, 'asymmetric-underdog-inversion', 'v1')
//   getFinding('strategic', 2026, 'asymmetric-underdog-inversion')  // → latest
//
// To add v2 of a finding:
//   1. Create features/findings/<slug>-v2.js exporting FINDING_META with
//      version: 'v2', prevVersion: 'lab:finding/.../v1', whatChanged: '...'
//   2. Update v1's FINDING_META: revisedBy: 'lab:finding/.../v2',
//      lifecycle: 'revised'
//   3. Register both versions below
//
// To retract a finding:
//   1. Update its FINDING_META: lifecycle: 'retracted', retractionNote: '...'
//   2. The article still renders; a retraction overlay is shown
// ════════════════════════════════════════════════════════════════════

import AsymmetricUnderdogInversionFinding, {
  FINDING_META as asymmetricUnderdogMeta,
} from './asymmetric-underdog-inversion';

import KoreaHungaryDivergenceFinding, {
  FINDING_META as koreaHungaryMeta,
} from './korea-hungary-divergence';

import KoreaLockinFinding, {
  FINDING_META as koreaLockinMeta,
} from './korea-lockin';

import PositiveCaseSearchFinding, {
  FINDING_META as positiveCaseSearchMeta,
} from './positive-case-search';

import IsraelHarediFloorFinding, {
  FINDING_META as israelHarediFloorMeta,
} from './israel-haredi-floor';

import RefugiaTemplateFinding, {
  FINDING_META as refugiaTemplateMeta,
} from './refugia-template';

import SubstrateSymmetricRefugiaFinding, {
  FINDING_META as substrateSymmetricRefugiaMeta,
} from './substrate-symmetric-refugia';

import BabyBoomDetectiveFinding, {
  FINDING_META as babyBoomDetectiveMeta,
} from './baby-boom-detective';

import QuebecQuietRevolutionFinding, {
  FINDING_META as quebecQuietRevolutionMeta,
} from './quebec-quiet-revolution';

import ConstraintManeuverFinding, {
  FINDING_META as constraintManeuverMeta,
} from './constraint-maneuver';

export const FINDING_REGISTRY = {
  'strategic/2026/asymmetric-underdog-inversion/v1': {
    component: AsymmetricUnderdogInversionFinding,
    meta: asymmetricUnderdogMeta,
  },
  'popdec/2026/korea-hungary-divergence/v1': {
    component: KoreaHungaryDivergenceFinding,
    meta: koreaHungaryMeta,
  },
  'popdec/2026/korea-lockin/v1': {
    component: KoreaLockinFinding,
    meta: koreaLockinMeta,
  },
  'popdec/2026/positive-case-search/v1': {
    component: PositiveCaseSearchFinding,
    meta: positiveCaseSearchMeta,
  },
  'popdec/2026/israel-haredi-floor/v1': {
    component: IsraelHarediFloorFinding,
    meta: israelHarediFloorMeta,
  },
  'popdec/2026/refugia-template/v1': {
    component: RefugiaTemplateFinding,
    meta: refugiaTemplateMeta,
  },
  'popdec/2026/substrate-symmetric-refugia/v1': {
    component: SubstrateSymmetricRefugiaFinding,
    meta: substrateSymmetricRefugiaMeta,
  },
  'popdec/2026/baby-boom-detective/v1': {
    component: BabyBoomDetectiveFinding,
    meta: babyBoomDetectiveMeta,
  },
  'popdec/2026/quebec-quiet-revolution/v1': {
    component: QuebecQuietRevolutionFinding,
    meta: quebecQuietRevolutionMeta,
  },
  'strategic/2026/constraint-maneuver/v1': {
    component: ConstraintManeuverFinding,
    meta: constraintManeuverMeta,
  },
};

/**
 * Lookup a finding by domain/year/slug + optional version.
 * If version is omitted, returns the latest version.
 */
export function getFinding(domain, year, slug, version) {
  if (version) {
    const key = `${domain}/${year}/${slug}/${version}`;
    return FINDING_REGISTRY[key] || null;
  }
  // No version: find the highest version for this slug
  const baseKey = `${domain}/${year}/${slug}`;
  const matchingKeys = Object.keys(FINDING_REGISTRY).filter((k) => k.startsWith(`${baseKey}/`));
  if (matchingKeys.length === 0) return null;
  matchingKeys.sort((a, b) => {
    const va = parseInt(a.split('/').pop().slice(1), 10) || 0;
    const vb = parseInt(b.split('/').pop().slice(1), 10) || 0;
    return vb - va; // descending — latest first
  });
  return FINDING_REGISTRY[matchingKeys[0]];
}

/**
 * Get all versions of a finding by domain/year/slug.
 * Returns an array sorted v1 → vN (ascending).
 */
export function getAllVersions(domain, year, slug) {
  const baseKey = `${domain}/${year}/${slug}`;
  const matchingKeys = Object.keys(FINDING_REGISTRY).filter((k) => k.startsWith(`${baseKey}/`));
  matchingKeys.sort((a, b) => {
    const va = parseInt(a.split('/').pop().slice(1), 10) || 0;
    const vb = parseInt(b.split('/').pop().slice(1), 10) || 0;
    return va - vb; // ascending
  });
  return matchingKeys.map((k) => ({ key: k, ...FINDING_REGISTRY[k] }));
}

export function listFindings() {
  // Return only the latest version of each slug (for finding listings).
  const seenSlugs = new Set();
  const result = [];
  // Iterate in descending order so we hit latest first
  const keys = Object.keys(FINDING_REGISTRY).sort((a, b) => {
    const va = parseInt(a.split('/').pop().slice(1), 10) || 0;
    const vb = parseInt(b.split('/').pop().slice(1), 10) || 0;
    return vb - va;
  });
  for (const key of keys) {
    const parts = key.split('/');
    const slugKey = parts.slice(0, 3).join('/'); // domain/year/slug
    if (seenSlugs.has(slugKey)) continue;
    seenSlugs.add(slugKey);
    const entry = FINDING_REGISTRY[key];
    result.push({
      path: `/findings/${slugKey}`,
      versionedPath: `/findings/${key}`,
      ...entry.meta,
    });
  }
  return result;
}
